import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import PropertyMap, { CATEGORIES } from '../components/PropertyMap';
import propertiesService from '../services/propertiesService';
import { getDirections } from '../services/directionsService';
import './Properties.css';

const centroid = (boundary) => {
  if (!boundary?.length) return null;
  return {
    lat: boundary.reduce((s, p) => s + p.lat, 0) / boundary.length,
    lng: boundary.reduce((s, p) => s + p.lng, 0) / boundary.length,
  };
};

function PropertyDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    propertiesService.getProperty(id)
      .then(data => setProperty(data))
      .catch(err => {
        if (err?.status === 404) setError('Property not found.');
        else setError('Could not load property. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="properties-page"><div className="container"><p>Loading…</p></div></div>;
  if (error)   return (
    <div className="properties-page"><div className="container">
      <p className="error-message">{error}</p>
      <button className="button secondary-button" onClick={() => navigate('/dashboard')}>← Go Back</button>
    </div></div>
  );

  return (
    <div className="properties-page">
      <div className="container">

        <div className="detail-page-header">
          <h1>{property.name}</h1>
          <button
            className="button directions-button"
            onClick={() => getDirections(centroid(property.boundary), property.name)}
          >
            <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>directions_car</span>
            {' '}Get Directions
          </button>
          <button className="button edit-button" onClick={() => navigate(`/property/${id}/edit`, { state: { property } })}>
            Edit
          </button>
        </div>

        <PropertyMap boundary={property.boundary} features={property.features} />

        <div className="property-card" style={{ marginTop: '1.25rem' }}>
          <div className="property-info">

            {property.description && (
              <p className="property-description">{property.description}</p>
            )}

            {property.notes && (
              <div className="emergency-notes">
                <span className="emergency-label">⚠️ Emergency Notes</span>
                <p>{property.notes}</p>
              </div>
            )}

            {property.is_livestock_staging_area && (
              <div className="staging-notes">
                <span className="staging-label">🐄 Livestock Staging Area</span>
                {property.livestock_staging_notes ? (
                  <p>{property.livestock_staging_notes}</p>
                ) : (
                  <p className="staging-empty">This property is available to house or store animals during an emergency. Contact the owner for details.</p>
                )}
                {(property.owner_name || property.owner_phone || property.owner_email) && (
                  <div className="staging-contact">
                    <strong>Owner contact:</strong>
                    {property.owner_name && <p>{property.owner_name}</p>}
                    {property.owner_phone && <p>📞 {property.owner_phone}</p>}
                    {property.owner_email && <p>✉️ {property.owner_email}</p>}
                  </div>
                )}
              </div>
            )}

            {property.features?.length > 0 && (
              <div className="features-summary">
                {CATEGORIES.map(c => {
                  const group = property.features.filter(f => f.category === c.key);
                  return group.length > 0 ? (
                    <div key={c.key} className="feature-group">
                      <span className="feature-group-label" style={{ color: c.color }}>
                        {c.emoji} {c.label}
                      </span>
                      <ul>
                        {group.map(f => (
                          <li key={f.id}>
                            <strong>{f.label}</strong>
                            {f.notes && <span className="feature-note"> — {f.notes}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null;
                })}
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}

export default PropertyDetail;
